import React from 'react'
import styled from '@emotion/styled'

import { mediaQueries } from '../theme'
import Container from '../components/shared/container'
import Layout from '../components/layout'
import PageMetadata from '../components/page-metadata'
import {
  Article,
  ArticleHeading,
  Heading,
  P,
  Ul,
  Ol,
} from '../components/shared/type'
import BaseLink from '../components/shared/link'

const InnerContainer = styled(Container)`
  margin-bottom: ${p => p.theme.space[18]};

  ${mediaQueries.lg} {
    margin-bottom: ${p => p.theme.space[29]};
  }
`

const Section = styled.section`
  max-width: 48.75rem;
`

const Link = styled(BaseLink)`
  padding: 0;
`

const TermsPage = ({ location }) => (
  <Layout location={location}>
    <PageMetadata
      title="Get Familiar with the Getform Terms of Service"
      description="Find out how the Getwebcraft Limited terms of service govern your access to and use of Getform."
      keywords="getform terms of service"
    />
    <InnerContainer>
      <Section>
        <Heading>Terms of service</Heading>
        <Article>
          <ArticleHeading>Introduction</ArticleHeading>
          <P>
            Getform´s products and services are provided by Getwebcraft Limited
            (referred to as “Getwebcraft”, “Getform”, “we”, “us”, “our”). These
            Terms of service (referred to as the “Terms”) govern your access to
            and use of Getform’s website and services (referred to as the
            “Services”). By accessing the URL&nbsp;
            <Link variant="fill" to="/">
              https://getform.com/
            </Link>
            &nbsp;or any other subdomains, (referred to as the “Site”) or using
            our Services, you agree to be bound by these Terms and by our
            Privacy Policy, which are impliedly included into these Terms.
          </P>

          <P>
            The terms ‘<strong>us</strong>’, ‘<strong>its</strong>’, ‘
            <strong>ours</strong>’ and/or ‘<strong>we</strong>’, as used herein,
            shall refer to us, Getform and/or our affiliates, assignees,
            successors and/or brands.
          </P>
        </Article>
        <Article>
          <ArticleHeading>Changes to the Terms</ArticleHeading>
          <P>
            Getwebcraft reserves the right to modify these Terms at any time.
            Your continued use of the Service following the posting of any
            changes to these Terms constitutes acceptance of those changes. As a
            general rule, we will try to not diminish the functionalities
            available to your Service tier, and in the case of increased or
            newly available functionalities, we will inform you before their
            activation, including the timeframe to accept new payment terms.
          </P>
          <P>
            Therefore, we ask that you check and review these Terms for such
            changes on an occasional basis. Should you not agree to any
            provision of these Terms or any changes we make thereof, we ask and
            advise that you do not use or continue to access the Site
            immediately.
          </P>
        </Article>
        <Article>
          <ArticleHeading>Registration</ArticleHeading>
          <P>
            You may browse the Site and view our content without registering,
            but as a condition to using certain aspects of the Service, you are
            required to register with Getform and provide accurate, truthful,
            and complete registration information (including, but not limited to
            your email address and a password you will use to access the
            Service) and to keep your registration information accurate and
            up-to-date.{' '}
          </P>
          <P>
            You will sign up with your email address and full name. You will be
            prompted to make a user ID. Your user ID must not contain spaces,
            symbols, obscene or profane words, email addresses, website
            addresses or representations of email addresses or website
            addresses, or our business name. You will be sent a verification
            email and once we are able to verify your email address, you will be
            bound by these terms until such time you delete your account with
            us.
          </P>
          <P>
            You are responsible for maintaining the confidentiality of any
            account name and password provided to you. You are solely
            responsible for uses of any account provided to you, whether or not
            authorized by you. You agree to immediately notify us of any
            unauthorized use of any account of yours.
          </P>
          <P>
            We reserve the right, at our sole and final discretion, to
            deactivate, freeze, suspend or terminate any account upon any
            factual or alleged breach of these Terms. You must notify us
            immediately of any change in your eligibility to use our platform,
            or if you suspect a breach of security or unauthorized use of your
            account.
          </P>
          <P>
            You acknowledge and agree that we may report any activity that we
            believe may violate any law to law enforcement, regulators or other
            relevant third parties, and that any violation of the aforementioned
            provisions may result in the immediate termination of your access to
            the Site or our Services.
          </P>
          <P>
            Getwebcraft does not knowingly provide its Services to persons under
            the age of eighteen (18). Accordingly, we reserve the right to
            request any and all applicable proof of identification and consent
            proof from our users, at any moment, without prior notice, and at
            our sole and final discretion, in line with our Privacy Policy
          </P>
          <P>
            Getwebcraft does not knowingly collect any kind of information from
            any person under the age of thirteen (13) and will delete any
            related information thereto.
          </P>
        </Article>
        <Article>
          <ArticleHeading>User Code of Conduct</ArticleHeading>
          <P>
            As our user, you agree not to undertake, motivate, or facilitate the
            use or access of the Site or the Services for the following:
          </P>
          <Ul>
            <li>
              incorporate into your online forms created using our Services any
              text or other content that is not created by you, not provided by
              Getform or you are not otherwise permitted to use;
            </li>
            <li>
              post any misleading or incorrect information in any online forms
              created using our Services;
            </li>
            <li>
              publish any material that contains sexually related text,
              photographs or other content, or content that is defamatory,
              obscene, indecent, threatening, abusive or hateful;
            </li>
            <li>
              include in Getform forms any material, including, but not limited
              to text, the inclusion of which is in violation of any other
              party’s rights, including, but not limited to, copyrights and
              privacy and publicity rights;
            </li>
            <li>
              violate any local or other law or regulation, or any order of a
              court;
            </li>
            <li>
              use manual or automated software, devices, scripts robots, other
              means or processes to access, “scrape,” “crawl” or “spider” any
              web pages or other services contained in the Website, Services or
              Content;
            </li>
            <li>
              copy, store or otherwise access any information contained on the
              Website, Services or Content for purposes not expressly permitted
              by these Terms;
            </li>
            <li>
              infringe the rights of any person or entity, including but not
              limited to, their intellectual property, privacy, publicity or
              contractual rights;
            </li>
            <li>
              interfere with or damage our Website or Services, including,
              without limitation, through the use of viruses, cancel bots,
              Trojan horses, harmful code, flood pings, denial-of-service
              attacks, packet or IP spoofing, forged routing or electronic mail
              address information or similar methods or technology;
            </li>
            <li>
              systematically retrieve data or other content from our Website or
              Services to create or compile, directly or indirectly, in single
              or multiple downloads, a collection, compilation, database,
              directory or the like, whether by manual methods, through the use
              of bots, crawlers, or spiders, or otherwise;
            </li>
            <li>
              use, display, mirror or frame the Website, or any individual
              element within the Website, or use, Our name, any Our trademark,
              logo or other proprietary information, or the layout and design of
              any page or form contained on a page, without Our express written
              consent; or
            </li>
            <li>
              <P>
                advocate, encourage, or assist any third party in doing any of
                the foregoing.
              </P>
              <P>
                Any content you upload will be considered non-confidential and
                non-proprietary. You retain all of your ownership rights in your
                content, but you are required to grant us a royalty-free,
                perpetual, irrevocable and non-exclusive licence to use,
                reproduce, distribute, display, modify and edit the content in
                connection with the Website.{' '}
              </P>
              <P>
                We have the right to remove any online form you create using our
                Services if, in our opinion, your online form does not comply
                with the content standards set out in these terms.
              </P>
              <P>
                We reserve the right to remove any post which breaches these
                terms and terminate the membership of the user.
              </P>
            </li>
          </Ul>
        </Article>
        <Article>
          <ArticleHeading>Paid Subscriptions and Refunds</ArticleHeading>
          <Ul>
            <li>We offer three subscription plans — Free, Pro, Pro Max.</li>
            <li>
              These subscription plans are rolling with a choice to make
              payments either monthly or annually.
            </li>
            <li>
              If you choose to upgrade to a paid subscription-based plan, you
              authorize Getwebcraft or its payment processor to charge the
              applicable recurring subscription fees to your designated billing
              payment method.
            </li>
            <li>
              Charges from Getform will appear on your bank statement as
              GETFORM.COM.
            </li>
            <li>
              All paid subscriptions will automatically renew for the same term,
              monthly or annually, using the payment method on file unless you
              change or discontinue the Service. If you are not using the
              service, we will not consider it as a discontinuance of the
              Service. To discontinue the Service, please notify of this by
              changing your subscription plan in the Billing section of your
              account.
            </li>
            <li>
              Price is based on the selected subscription plan and the number of
              responses received via the Service, regardless of the number of
              online forms active during the period of subscription and the
              number of extra users added to the account.
            </li>
            <li>All upgrades and downgrades are effective immediately.</li>
            <li>
              You have the right to request a full refund within ten (10) days
              of a monthly subscription purchase and within thirty (30) days of
              an annual subscription purchase. To request a refund, contact us
              at&nbsp;
              <Link href="mailto:support@getform.com" variant="fill">
                support@getform.com
              </Link>
              . We will review your request and at our discretion approve all
              requests that have followed the correct process.
            </li>
            <li>All refunds are issued to the method of original payment.</li>
            <li>
              If you cancel the Services, your cancellation takes effect
              immediately. You will not be entitled to any refund of any unused
              part of your payment for the Services.
            </li>
          </Ul>
        </Article>
        <Article>
          <ArticleHeading>Payment Processing Terms</ArticleHeading>
          <Ul>
            <li>
              We use payment platforms provided by globally accepted third-party
              payment processors. Accordingly, the processing of payments will
              be subject to the fees, terms, conditions and privacy policies of
              such payment processors. Getwebcraft is not responsible for any
              and all errors, fees and currency conversion fees by the payment
              processors.
            </li>
            <li>
              Such third-party payment transaction processors have enacted their
              own terms of use, user agreements and privacy policies, which will
              govern their provision of services to you in respect to the
              information we are required to provide to them for your
              purchase-related transactions. For these providers, we recommend
              that you read their terms and conditions and their privacy
              policies so you can understand the manner in which your personal
              information will be handled by these providers.
            </li>
            <li>
              If you elect to use a direct payment gateway in order to complete
              your purchase, then the third-party payment processors will
              collect and store credit card data. Your purchase transaction data
              will only be stored as long as it is necessary to complete your
              transaction with us. After such order or transaction is complete,
              your purchase transaction information will be deleted.
            </li>
          </Ul>
        </Article>
        <Article>
          <ArticleHeading>Disputes & Chargebacks</ArticleHeading>
          <P>
            You must provide us with valid and current billing information.
            Except as expressly set forth herein, all payment charges are final
            and non-cancelable. If we detect any chargeback or if any payment is
            not received by us or our payment processors for any reason, you
            will promptly pay us any and all amounts due to us upon notice. Any
            failure or inability by us to process any payment hereunder does not
            relieve you from your payment obligations.
          </P>
          <P>
            A chargeback is typically caused by a situation when a customer
            disputes a charge that appears on their bank or payment processing
            statement. A chargeback may result in the reversal of a transaction,
            with the amount charged back to you. You can be assessed chargebacks
            for: (i) customer disputes; (ii) unauthorized or improperly
            authorized transactions; (iii) transactions that do not comply with
            payment processor network rules or are allegedly unlawful or
            suspicious; or (iv) any reversals for any reason by our payment
            processor or the institutions handling the transaction.
          </P>
          <P>
            When a chargeback is issued, you are immediately liable to
            Getwebcraft for the full amount of payment of the chargeback, plus
            any associated fees, fines, expenses or penalties (including those
            assessed by our payment processor or the financial institutions
            handling the transaction). Accordingly, you hereby represent and
            warrant that you expressly appoint Getwebcraft as your agent, with
            full power to recover these amounts by debiting your account or
            setting off any amounts owed to you by us.
          </P>
        </Article>
        <Article>
          <ArticleHeading>Term, Termination</ArticleHeading>
          <P>
            The term hereof shall begin on the date that comes first among: (i)
            first access to the Site; (ii) your first access or execution of our
            Services; or (iii) Getwebcraft begins providing its Services to you.
          </P>
          <P>
            Getwebcraft does not guarantee that the Website, or any content on
            it, will always be available or be uninterrupted. We may
            discontinue, suspend, withdraw, or restrict the availability of all
            or any part of our Website for business and operational reasons. We
            will try to give you reasonable notice of any suspension or
            withdrawal.
          </P>
          <P>
            You are also responsible for ensuring that all persons who access
            our Website through your internet connection are aware of these
            terms of use and other applicable terms and conditions, and that
            they comply with them.
          </P>
          <P>
            Getwebcraft reserves the right to terminate your access to the
            Service or discontinue the Service (including without limitation,
            the availability of any feature, database, or content), without any
            advance notice. Getwebcraft may also impose limits on certain
            features and services or restrict your access to parts or all of the
            Service without notice or liability.
          </P>
          <P>
            These Terms, along with any and all licenses granted hereof may or
            will automatically terminate if you breach any of the terms and
            conditions contained herein. Upon termination for breach, your
            rights to use our Site, our Services and any information provided or
            generated thereby shall cease and you shall not be entitled to any
            compensation, credit, remedy or refund of any nature. In this case,
            please contact our support department in order to initiate the
            process of receiving a backup copy of your user data, if any,
            subject to the terms and conditions of our providers, such as Amazon
            Web Services.
          </P>
        </Article>
        <Article>
          <ArticleHeading>User Representations and Warranties</ArticleHeading>
          <P>
            You hereby represent, warrant and covenant to Getwebcraft that: (i)
            your use of our Site and Services, and all your uploaded and used
            data shall be at all times compliant with these Terms and all local,
            state, federal and international laws and regulations applicable to
            you and your organization; (ii) you have obtained all necessary
            rights, releases and permissions to provide any data to Getform and
            its affiliates, licensors and agents; and to grant the rights
            granted to Getwebcraft in these Terms, including without limitation
            any intellectual property rights or rights of publicity, privacy and
            any use, collection and disclosure authorized.
          </P>
        </Article>
        <Article>
          <ArticleHeading>No Endorsement</ArticleHeading>
          <P>
            The images, texts, posts, information, photographs and other content
            and media displayed on or through the Site and our Services are not
            necessarily available and any results therefrom – which we cannot
            ultimately control – are out of our ultimate and complete scope.
            Some or all of the images shown in the Site are licensed and/or
            purchased stock photos, and are only shown for illustration
            purposes. Some or all of the content displayed in the Site is
            undertaken by third parties, and does not reflect Getwebcraft team’s
            opinions, nor do Getwebcraft, its affiliates, subsidiaries,
            officers, employees and agents guarantee its actual veracity or make
            any endorsement thereof.
          </P>

          <P>
            You acknowledge and agree that Getwebcraft shall not be required to
            actively monitor nor exercise any editorial control whatsoever over
            the content of any message or material or information created,
            obtained or accessible through the Services or Site. Each registered
            user is solely responsible for the contents of their communications
            and may be held legally liable or accountable for the content of
            their comments or other material or information.
          </P>
        </Article>
        <Article>
          <ArticleHeading>Intellectual Property Rights</ArticleHeading>
          <P>
            The Site, the Services and all of the content displayed therein
            (including, but not limited to designs, text, graphics, pictures,
            information, the software, code, proprietary methods and systems
            used to provide the Site or Services) are the sole property of
            Getwebcraft and are, as such, fully protected by the appropriate
            international copyright and other intellectual property rights laws.
            Unless we expressly state otherwise or as allowed by our Terms of
            service, your rights do not include: (i) publicly performing or
            publicly displaying the Service, (ii) modifying or otherwise making
            any derivative uses of the Service or any portion thereof; (iii)
            downloading (other than page caching) of any portion of the Service
            or any information contained therein; (iv) reverse engineering or
            accessing the Service in order to build a competitive product or
            service; (v) using the Service other than for its intended purposes.
          </P>
          <P>
            Respective title holders may or may not be affiliated with us or our
            affiliates, partners and advertisers. No section hereof shall be
            construed as intent to grant to you any right transfer or interest
            in the Site or our Services, in whole or in part.
          </P>
          <P>
            You acknowledge and agree that any and all infringing use or
            exploitation of copyrighted content in the Site and/or the Services
            may cause us, our affiliates, licensors or content providers
            irreparable injury, which may not be remedied solely at law, and
            therefore our affiliates, licensors or content providers may seek
            remedy for breach of these Terms, either in equity or through
            injunctive or other equitable relief.
          </P>
          <P>
            Getwebcraft takes copyright infringement matters seriously and is
            ready to remove any allegedly or factually infringing content
            displayed on the Site upon due notice and request by the title
            holder.
          </P>
          <P>
            The following procedure will apply for any content displayed through
            the Site that allegedly infringes the intellectual property rights
            of you or of any third party. You must notify us of your claim with
            the subject: "Takedown Request". Once received, Getwebcraft will
            study and consider your claim and, if we have reasons to believe any
            content on the Site infringes on another’s copyright, Getwebcraft
            may delete it, disable or otherwise stop displaying it.
          </P>
          <P>
            Your notification claim must be in writing and must at least contain
            the following information: (i) your signature and identification, or
            the ones the person authorized to act on behalf of you or the title
            holder; (ii) a clear and concise description of the content of which
            its copyright has allegedly been infringed; (iii) contact
            information (e.g. address and email); and (iv) a statement, under
            penalty of perjury, indicating that you have a good faith belief
            that the information provided in your claim is true and accurate.
          </P>
        </Article>
        <Article>
          <ArticleHeading>Limited License</ArticleHeading>
          <P>
            In order to access our Services, Getwebcraft grants you a limited,
            non-exclusive, revocable and non-transferable license to utilize and
            access the Services. You are prohibited from duplicating,
            re-engineering, reverse engineering, modifying or otherwise using
            the Services, in whole or in part. Getwebcraft does not grant any
            express or implied right to you under any patents, trademarks,
            copyrights or trade secret information; and you shall have no right,
            either directly or indirectly, to own, use, loan, sell, rent, lease,
            license, sublicense, assign, copy, translate, modify, adapt, improve
            or create any new or derivative works from, or display, distribute,
            perform or in any way exploit any downloaded Services and computer
            applications, in whole or in part.
          </P>
        </Article>
        <Article>
          <ArticleHeading>User License</ArticleHeading>
          <P>
            In order to access and use our Services, You hereby grant
            Getwebcraft an unlimited, non-exclusive, sub-licensable, assignable,
            royalty-free, perpetual, irrevocable, for all the countries and
            territories through the world, right and license to use, host,
            store, reproduce, modify, create derivative works (such as those
            resulting from translations, adaptations or other changes),
            communicate, publish, publicly perform, publicly display and
            distribute such any content you may upload, disseminate, deliver,
            create or transfer any post, original audio file, message, chat,
            files uploaded, data inputted, e-mails sent, or otherwise any
            content delivered to Getwebcraft via the Site or the Services. You
            represent and warrant to Getwebcraft that you have all rights,
            authorizations or otherwise hold sufficient title for any and all
            content submitted to Getform as set forth herein.
          </P>
        </Article>
        <Article>
          <ArticleHeading>Feedback License</ArticleHeading>
          <P>
            In the event that you provide us any ideas, thoughts, suggested
            improvements or other feedback related to the Site or the Services,
            you agree we may use this Feedback to modify our services and that
            you will not be due any compensation, including any royalty related
            to the product or service that incorporates your Feedback. You grant
            to us a worldwide, royalty-free, fully paid, perpetual, irrevocable
            license to use, reproduce, modify, translate, distribute, perform,
            display, import, sell, offer for sale, make, have made and otherwise
            exploit the Feedback in any form, media, or technology, whether now
            known or hereafter developed, and to allow others to do the same.
            This is true unless we have entered into a separate agreement with
            you that provides otherwise.
          </P>
        </Article>
        <Article>
          <ArticleHeading>Disclaimer</ArticleHeading>
          <P>
            The Services and all included content are provided on an “as is” and
            “as available” basis without warranty of any kind, whether express
            or implied. Getwebcraft, and its affiliates, clients, agents,
            officers, licensors and/or distributors, do not make, and hereby
            disclaim, any and all express, implied or statutory warranties,
            either by statute, common law, custom, usage of trade, course of
            dealing or otherwise, however arising, including implied warranties
            of description, quality, fitness for a particular purpose,
            non-infringement, non-interference with use.
          </P>
        </Article>
        <Article>
          <ArticleHeading>Website Security</ArticleHeading>
          <P>
            Getwebcraft does not guarantee that the Website will be secure or
            free from bugs or viruses.
          </P>
          <P>
            You are responsible for configuring your information technology,
            computer programs and platform to access our Website. You should use
            your own virus protection software.
          </P>
          <P>
            You must not misuse our Website by knowingly introducing viruses,
            trojans, worms, logic bombs or other material that is malicious or
            technologically harmful. You must not attempt to gain unauthorized
            access to our Website, the server on which our Website is stored, or
            any server, computer or database connected to our Website. You must
            not attack our Website via a denial-of-service attack or a
            distributed denial-of service attack. By breaching this provision,
            you would commit a criminal offence. We will report any such breach
            to the relevant law enforcement authorities, and we will cooperate
            with those authorities by disclosing your identity to them. In the
            event of such a breach, your right to use our Website will cease
            immediately.
          </P>
          <P>
            We cannot guarantee the service without any faults. If there is any
            problem with the Website, users should contact us, and we will try
            to rectify/fix the issue as soon as reasonably practicable.
          </P>
        </Article>

        <Article>
          <ArticleHeading>Limitation of Liability</ArticleHeading>
          <P>
            Getwebcraft cannot be held liable for any damages. Getwebcraft shall
            not be liable for any statements or conduct of any third party using
            the Service. Getwebcraft assumes no responsibility for the
            timeliness, deletion, miss-delivery or failure to store any User
            content, communication or personal settings. You agree to indemnify
            and hold Getwebcraft, its officers, employees, affiliates,
            contractors, and suppliers, exempt from any claim or demand,
            including reasonable attorneys' fees, made by any third party due to
            or arising out of Content you submit, transmit, post or otherwise
            make available through Getwebcraft.
          </P>
          <P>
            To the maximum extent permitted by applicable law, Getwebcraft shall
            not be liable for any indirect, incidental, special, consequential
            or punitive damages, or any loss of profit or revenues, whether
            incurred directly or indirect, or any loss of data, use, good-will,
            or other intangible losses, resulting from (i) your access to or use
            of or inability to access or use the Service; (ii) any conduct or
            content of any third party on the Services, including without
            limitation, any defamatory, offensive or illegal conduct of other
            users or third parties; (iii) any content obtained from the
            Services; or (iv) unauthorized access, use or alteration of your
            transmissions or content. In no event shall the aggregate liability
            of Getwebcraft exceed the greater of one hundred U.S. dollars (U.S.
            $100.00) or the amount you paid Getwebcraft, if any, in the past six
            months for the Services giving rise to the claim. The limitations of
            this subsection shall apply to any theory of liability, whether
            based on warranty, contract, statute, tort (including negligence) or
            otherwise, and whether or not Getwebcraft has been informed of the
            possibility of any such damage, and even if a remedy set forth
            herein is found to have failed of its essential purpose.
          </P>
          <P>
            Legislation of some states/countries does not allow certain
            limitations of liability, and henceforth this limitation of
            liability shall apply to the fullest extent permitted by law in the
            applicable jurisdiction.
          </P>
        </Article>

        <Article>
          <ArticleHeading>Indemnification</ArticleHeading>
          <P>
            You shall indemnify, hold harmless, and defend Getform, its
            affiliates, clients, agents, officers, licensors, distributors
            and/or any authorized representatives, and the officers, directors
            and employees of each (jointly, the “
            <strong>Getform´s Indemnitees</strong>”) from and against any and
            all third party liabilities, claims, causes of action, suits,
            losses, damages, fines, judgments, settlements and expenses
            (including any and all reasonable outside attorneys’ fees and court
            costs) which may be suffered, made or incurred by any of such
            Getform´s Indemnitees arising out of or relating to: (i) any breach
            of any warranties, representations and/or covenants made by you
            hereunder (to the extent not arising substantially from any breach
            hereof by Getform); and/or (ii) any third party claim arising out of
            or in relation to Getform or use thereof in combination with your
            business platform, including without limitation, any claim that
            Getform violates, infringes, or misappropriates any proprietary or
            intellectual property right of any third party, including without
            limitation, any privacy right of any person, or violates any
            applicable law.
          </P>
        </Article>

        <Article>
          <ArticleHeading>Assignment</ArticleHeading>
          <P>
            These Terms, and any rights and licenses granted hereunder, may not
            be transferred or assigned by you, but may be assigned by
            Getwebcraft without restriction. Any attempted transfer or
            assignment in violation hereof shall be null and void. Any rights
            not expressly granted herein are thereby reserved. These terms will
            inure to the benefit of any successors of the parties. We reserve
            the right, at any time, to transfer some or all of Getform’ assets
            in connection with a merger, acquisition, reorganization or sale of
            assets or in the event of bankruptcy.
          </P>
        </Article>

        <Article>
          <ArticleHeading>Force Majeure</ArticleHeading>
          <P>
            Getform is no liable for any failure of performance on its
            obligations as set forth herein, where such failure arises from any
            cause beyond Getform' reasonable control, including but not limited
            to, electronic, power, mechanic or Internet failure, from acts of
            nature, forces or causes beyond our control, strikes, labor
            disputes, riots, insurrections, civil disturbances, shortages of
            labor or materials, fires, flood, storms, explosions, acts of God,
            war, governmental actions, orders of domestic or foreign courts or
            tribunals or non-performance of third parties.
          </P>
        </Article>

        <Article>
          <ArticleHeading>Headings</ArticleHeading>
          <P>
            The titles of paragraphs in these Terms are shown only for ease of
            reference and will not affect any interpretation therefrom.
          </P>
        </Article>

        <Article>
          <ArticleHeading>Entire Agreement</ArticleHeading>
          <P>
            These Terms set forth the entire agreement between the parties
            hereof and may not be altered or amended except in writing signed by
            each both parties.
          </P>
        </Article>

        <Article>
          <ArticleHeading>Equitable remedies</ArticleHeading>
          <P>
            You hereby acknowledge and agree that if these Terms are not
            specifically enforced, Getform will be irreparably damaged, and
            therefore you agree that Getform shall be entitled, without bond,
            other security or proof of damages, to appropriate equitable
            remedies with respect to your breach of any of these Terms, in
            addition to any other available remedies.
          </P>
        </Article>

        <Article>
          <ArticleHeading>Language</ArticleHeading>
          <P>
            These Terms may be translated into other languages, but English
            shall be and remain the official language of this agreement and in
            any conflict between the English language version and any other
            version, the English language version shall control.
          </P>
        </Article>

        <Article>
          <ArticleHeading>Newsletters</ArticleHeading>
          <P>
            The Site may allow you to subscribe to our newsletter service, which
            may be provided by us or through an authorized third party. Through
            our newsletters, you may receive information according to your
            subscriber preferences. As our subscriber, you will receive a
            conspicuous communication indicating any subscription and you will
            be able to select the amount or type of emails received by you. If
            you wish to unsubscribe, you will find the ‘Unsubscribe’ and similar
            links on our communications.
          </P>
        </Article>

        <Article>
          <ArticleHeading>No Embargo</ArticleHeading>
          <P>
            You hereby represent and warrant that: (i) you are not located in a
            country that is subject to a U.S.A., Canada, European Union or other
            government’s embargo, or that has been designated as a “terrorist
            supporting” country; and (ii) you are not listed on any government
            list of prohibited or restricted parties or activities.
          </P>
        </Article>

        <Article>
          <ArticleHeading>EU-US Privacy Shield</ArticleHeading>
          <P>
            By registering a Getform account and disclosing any personally
            identifying information to us, you agree to our Terms of Service and
            our Privacy Policy, including the collection, processing, storage,
            and disclosure of such personally identifiable information to our
            affiliates, partners, and clients. That implies your express consent
            for inclusion into our newsletters, updates, and follow-ups. For
            more information, please read our Privacy Policy.
          </P>
          <P>
            It is made of note that the Court of Justice of the EU has validated
            the use of Standard Contractual Clauses or SCCs as a mechanism for
            transferring data outside the EU; therefore, when we process any of
            Getform users’ PII to further store it on Amazon servers or other
            processing contractors outside the EU or UK, we perform it under
            full compliance with the GDPR, and our customers can rely on the
            SCCs included in our Terms of Service.
          </P>
          <P>
            The SCCs are available here for all of our users in various regions
            of the world including the EU, UK and USA, forming an integral part
            of these Terms of service.
          </P>
        </Article>

        <Article>
          <ArticleHeading>No Relationship</ArticleHeading>
          <P>
            You and Getwebcraft are independent contractors, and no agency,
            partnership, joint venture, employee-employer, or
            franchiser-franchisee relationship is intended or created by these
            Terms.
          </P>
        </Article>

        <Article>
          <ArticleHeading>Severability</ArticleHeading>
          <P>
            These Terms, together with the Privacy policy and any amendments and
            any additional agreements you may enter into with Getwebcraft in
            connection with the Services, shall constitute the entire agreement
            between you and Getwebcraft concerning the Services. If any
            provision of these Terms is deemed invalid, then that provision will
            be limited or eliminated to the minimum extent necessary, and the
            remaining provisions of the Terms will remain in full force and
            effect.
          </P>
        </Article>

        <Article>
          <ArticleHeading>Waiver</ArticleHeading>
          <P>
            No waiver of any term of these Terms shall be deemed a further or
            continuing waiver of such term or any other term, and Getwebcraft's
            failure to assert any right or provision under these Terms shall not
            constitute a waiver of such right or provision.
          </P>
        </Article>

        <Article>
          <ArticleHeading>Governing Law</ArticleHeading>
          <P>
            These Terms are governed in accordance with the laws of Cyprus and
            are subject to the jurisdiction of the courts of Cyprus.
          </P>
        </Article>

        <Article>
          <ArticleHeading>Contact us</ArticleHeading>
          <P>
            If you have any questions or concerns about this Privacy policy,
            please feel free to contact us.
          </P>

          <P>
            <Link variant="fill" href="mailto:contact@getform.com">
              contact@getform.com
            </Link>
            <br />
            Klimentos 41-43, Klimentos Tower, Flat/Office 25, 1061, Nicosia,
            Cyprus
          </P>

          <P>
            <strong>
              Date of last effective update is September 13, 2023.
            </strong>
          </P>
        </Article>

        <Article>
          <ArticleHeading>Standard Contractual Clauses (SCCs)</ArticleHeading>
          <P>
            When Getwebcraft transfers the Personal Information of EU or UK
            customers to its Amazon servers based in the US, Getwebcraft ensures
            that it has taken additional measures to comply with European data
            protection laws. We make sure that there is a level of protection
            deemed adequate by the European Commission or that the relevant
            &nbsp;
            <Link
              href="https://commission.europa.eu/law/law-topic/data-protection/international-dimension-data-protection/standard-contractual-clauses-scc_en"
              variant="fill"
            >
              Standard Contractual Clauses
            </Link>
            &nbsp;are in place.
          </P>
        </Article>

        <Article>
          <ArticleHeading>Security of Processing</ArticleHeading>
          <P>
            Getwebcraft, as the data importer, shall take all necessary
            technical and organizational measures to ensure a level of security
            appropriate to the risk, in accordance with the state of the art and
            the cost of implementation, to protect the personal data from
            unauthorized access, use, disclosure, alteration, or destruction.
          </P>
        </Article>

        <Article>
          <ArticleHeading>Rights of Data Subjects</ArticleHeading>
          <P>
            The data importer shall ensure that data subjects have the following
            rights:
          </P>
          <Ul>
            <li>
              The right to be informed about the processing of their personal
              data.
            </li>
            <li>The right to access their personal data.</li>
            <li>The right to rectify their personal data.</li>
            <li>
              The right to erase their personal data (the right to be
              forgotten).
            </li>
            <li>
              The right to restrict the processing of their personal data.
            </li>
            <li>
              The right to object to the processing of their personal data.
            </li>
            <li>The right to data portability.</li>
            <li>
              The right to lodge a complaint with a supervisory authority.
            </li>
          </Ul>
        </Article>

        <Article>
          <ArticleHeading>Dispute Resolution</ArticleHeading>
          <P>
            If a dispute arises between the data exporter and the data importer
            regarding the processing of personal data under these SCCs, the
            parties shall first attempt to resolve the dispute through
            negotiation. If the dispute cannot be resolved through negotiation,
            the parties may submit the dispute to binding arbitration in
            accordance with the rules of the International Chamber of Commerce.
          </P>
        </Article>

        <Article>
          <ArticleHeading>UK Addendum</ArticleHeading>
          <P>
            The data importer shall comply with the following additional
            provisions:
          </P>
          <Ol counter="number">
            <li>
              The data importer shall not transfer the personal data to a third
              country or an international organization unless:
              <Ul>
                <li>
                  The third country or international organization ensures an
                  adequate level of protection for the personal data in
                  accordance with the GDPR; or
                </li>
                <li>
                  The transfer is made in accordance with Article 49 of the
                  GDPR.
                </li>
              </Ul>
            </li>
            <li>
              The data importer shall notify the data exporter without undue
              delay if it becomes aware of any unauthorized access to or use of
              the personal data or any other breach of security safeguards.
            </li>
            <li>
              The data importer shall cooperate with the data exporter to
              investigate any such unauthorized access or use or breach of
              security safeguards and to take appropriate remedial measures.
            </li>
          </Ol>
        </Article>

        <Article>
          <ArticleHeading>Entire Agreement</ArticleHeading>
          <P>
            These SCCs constitute the entire agreement between the parties with
            respect to the subject matter hereof and supersede all prior or
            contemporaneous communications, representations, or agreements,
            whether oral or written.
          </P>
        </Article>

        <Article>
          <ArticleHeading>Waiver</ArticleHeading>
          <P>
            No waiver of any provision of these SCCs shall be effective unless
            in writing and signed by both parties.
          </P>
        </Article>
      </Section>
    </InnerContainer>
  </Layout>
)

export default TermsPage
